import React, { useState } from 'react';
import type { CaseEntry, CaseEntryType, CaseEntryUnit } from '../../../types/caseEntry';
import type { QuoteLine } from '../../../types/quote';
import { entrySubtotal, sortEntries } from '../../../utils/caseEntries';
import { formatQuantity, guessEntryType } from '../../../utils/caseEstimate';
import { formatKr, lineSubtotal } from '../../../utils/quote';
import { parseAmount, toInput, todayIso } from './useCaseEntryForm';
import { EntryRow } from './EntryRow';
interface CostSectionProps {
  title: string;
  /** Sagstyper der hører til sektionen. */
  types: CaseEntryType[];
  showLabel?: boolean;
  emptyText: string;
  estimateLines: QuoteLine[];
  allEntries: CaseEntry[];
  saving: boolean;
  onPersist: (next: CaseEntry[]) => Promise<boolean>;
  onEdit: (entry: CaseEntry) => void;
  onDelete: (entry: CaseEntry) => void;
}

/** Linjer med "fast pris" sammenlignes på beløb; øvrige på antal. */
const isFixed = (unit: string) => unit === 'fast pris';

/**
 * Tilbuddets linjer som estimat med det faktiske antal ved siden af, og
 * derunder linjer tilføjet undervejs. Tilbuddet ændres aldrig herfra.
 */
export function CostSection({
  title,
  types,
  showLabel = false,
  emptyText,
  estimateLines,
  allEntries,
  saving,
  onPersist,
  onEdit,
  onDelete
}: CostSectionProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rowError, setRowError] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const quotedIds = new Set(estimateLines.map(line => line.id));
  const linkedFor = (lineId: string) => allEntries.find(entry => entry.quoteLineId === lineId);
  const extras = sortEntries(allEntries.filter(entry => types.includes(entry.type) && (!entry.quoteLineId || !quotedIds.has(entry.quoteLineId))));
  const actualValue = (line: QuoteLine, entry?: CaseEntry) => entry ? isFixed(line.unit) ? entrySubtotal(entry) : entry.quantity : undefined;
  async function setActual(line: QuoteLine, value: number) {
    const linked = linkedFor(line.id);
    if (linked && value === actualValue(line, linked)) return;
    const now = new Date().toISOString();
    const fixed = isFixed(line.unit);
    const quantity = fixed ? 1 : value;
    const unitPrice = fixed ? value : line.unitPrice;
    let next: CaseEntry[];
    if (value === 0) {
      if (!linked) return;
      next = allEntries.filter(entry => entry.quoteLineId !== line.id);
    } else if (linked) {
      next = allEntries.map(entry => entry.id === linked.id ? {
        ...entry,
        quantity,
        unitPrice,
        updatedAt: now
      } : entry);
    } else {
      next = [...allEntries, {
        // Fast id pr. tilbudslinje, så et nyt forsøg aldrig laver en dublet.
        id: `entry-q-${line.id}`,
        type: guessEntryType(line),
        date: todayIso(),
        description: line.description || title,
        quantity,
        unit: line.unit as CaseEntryUnit,
        unitPrice,
        vatRate: line.vatRate,
        quoteLineId: line.id,
        createdAt: now,
        updatedAt: now
      }];
    }
    await onPersist(next);
  }
  async function commit(line: QuoteLine) {
    const raw = drafts[line.id];
    if (raw === undefined) return;
    const value = raw.trim() === '' ? 0 : parseAmount(raw);
    if (!Number.isFinite(value) || value < 0) {
      setRowError({
        id: line.id,
        text: isFixed(line.unit) ? 'Skriv et gyldigt beløb, fx 450.' : 'Skriv et gyldigt antal, fx 3 eller 2,5.'
      });
      return;
    }
    setRowError(null);
    setDrafts(({
      [line.id]: _removed,
      ...rest
    }) => rest);
    await setActual(line, value);
  }
  if (estimateLines.length === 0 && extras.length === 0) {
    return <p className="text-sm text-ink-muted" data-id="element-145" data-mp="2sf1wufio1ggm">{emptyText}</p>;
  }
  return <div className="space-y-1" data-id="element-146" data-mp="2of1l52mybjd9">
      {estimateLines.length > 0 && <ul className="divide-y divide-line/70" data-id="element-147" data-mp="3kxpcvgit25au">
          {estimateLines.map(line => {
        const linked = linkedFor(line.id);
        const fixed = isFixed(line.unit);
        const value = drafts[line.id] ?? toInput(actualValue(line, linked));
        const error = rowError?.id === line.id ? rowError.text : '';
        return <li key={line.id} className="py-2.5" data-id="element-148" data-mp="35lg0h5rtlyv0">
                <p className="text-[15px] text-ink" data-id="element-149" data-mp="2rp2qehpm8zqk">{line.description || title}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm" data-id="element-150" data-mp="2voutwg05kfp2">
                  <span className="whitespace-nowrap text-ink-muted" data-id="element-151" data-mp="3jep8b204ecbf">
                    Estimeret: {fixed ? formatKr(lineSubtotal(line)) : `${formatQuantity(line.quantity || 0)} ${line.unit}`}
                  </span>
                  <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-ink-muted" data-id="element-152" data-mp="3am40ggy11q1d">
                    Faktisk:
                    <input inputMode="decimal" placeholder="0" value={value} disabled={saving} aria-label={`${fixed ? 'Faktisk beløb' : 'Faktisk antal'} for ${line.description}`} onChange={event => setDrafts(current => ({
                ...current,
                [line.id]: event.target.value
              }))} onBlur={() => commit(line)} onKeyDown={event => {
                if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
              }} aria-invalid={error ? true : undefined} className={`h-9 w-20 rounded-lg border bg-white px-2 text-right text-[15px] text-ink transition-colors duration-150 focus:border-accent focus:outline-none ${error ? 'border-danger' : 'border-line hover:border-ink/25'}`} data-id="element-153" data-mp="3eme3bkqiw0c0" />
                    <span data-id="element-154" data-mp="1hoqdrvl2dn32">{fixed ? 'kr.' : line.unit}</span>
                  </label>
                  {!fixed && <span className="whitespace-nowrap text-ink-muted" data-id="element-155" data-mp="2y2gj15nx6hea">Pris: {formatKr(line.unitPrice)}</span>}
                  {!linked && <button type="button" disabled={saving} onClick={() => setActual(line, fixed ? lineSubtotal(line) : line.quantity || 0)} className="whitespace-nowrap text-sm font-medium text-accent transition-colors duration-150 hover:text-accent-deep disabled:opacity-50" data-id="element-156" data-mp="308wkl4vg9y06">
                      Brug estimat
                    </button>}
                </div>
                {error && <p role="alert" className="mt-1 text-xs text-danger" data-id="element-157" data-mp="2mi1l2j6uct99">
                    {error}
                  </p>}
              </li>;
      })}
        </ul>}

      {extras.length > 0 && <div data-id="element-158" data-mp="2w84bcbmkvt3w">
          {estimateLines.length > 0 && <p className="pt-2 text-xs font-medium text-ink-muted" data-id="element-159" data-mp="1ys76nx6viwmw">Tilføjet undervejs</p>}
          <ul className="divide-y divide-line/70" data-id="element-160" data-mp="23pp70z6ts3ja">
            {extras.map(entry => <EntryRow key={entry.id} entry={entry} showLabel={showLabel} onEdit={onEdit} onDelete={onDelete} data-id="element-161" data-mp="kvw21unhmnun" />)}
          </ul>
        </div>}
    </div>;
}